import { IntelligenceFilterId } from '../types';

// The eight filter categories of the Customer Insights panel, their option lists and their defaults.
// The set, the order and every option are the design's, read off the eight `div.filterCard` exports
// (design/filter-panel-spec-2026-08-11.md § 4) and corroborated against the live file
// (design/figma-live-read-2026-08-11.md § 2b). Option ids are stable snake_case identifiers: they
// travel into the card data seam's cache key, so renaming one orphans a cached read. PLA-8815.

export interface FilterCategoryDescriptor {
  id: IntelligenceFilterId;
  optionIds: string[];
  defaultOptionId: string;
}

// A category value is one option id, optionally carrying that option's own sub-value after a colon
// (`age_band:25-34`, `custom:2026-07-08..2026-08-08`). Demographics needs a dimension AND a value,
// and a custom date range needs two dates, but `IntelligenceFilters` is
// `Partial<Record<IntelligenceFilterId, string>>` — one string per category. Encoding into that one
// string keeps both the shipped union and the shipped `cacheKeyFor` serializer untouched, where a
// wider value type would have to be narrowed by every consumer. PLA-8815.
const VALUE_SEPARATOR = ':';

export const encodeFilterValue = (optionId: string, subValue?: string): string =>
  subValue ? `${optionId}${VALUE_SEPARATOR}${subValue}` : optionId;

export const decodeFilterValue = (
  value?: string
): { optionId: string; subValue: string | undefined } => {
  if (!value) {
    return { optionId: '', subValue: undefined };
  }

  const separatorAt = value.indexOf(VALUE_SEPARATOR);

  if (separatorAt === -1) {
    return { optionId: value, subValue: undefined };
  }

  return {
    optionId: value.slice(0, separatorAt),
    subValue: value.slice(separatorAt + 1) || undefined
  };
};

export const DATE_RANGE_CUSTOM = 'custom';

// Both ends of a custom range live in one value; `..` cannot collide with an ISO date.
const RANGE_SEPARATOR = '..';

export const encodeCustomRange = (start: string, end: string): string =>
  encodeFilterValue(DATE_RANGE_CUSTOM, `${start}${RANGE_SEPARATOR}${end}`);

export const decodeCustomRange = (value?: string): [string, string] | null => {
  const { optionId, subValue } = decodeFilterValue(value);

  if (optionId !== DATE_RANGE_CUSTOM || !subValue) {
    return null;
  }

  const [start, end] = subValue.split(RANGE_SEPARATOR);

  return start && end ? [start, end] : null;
};

export const FILTER_CATEGORIES: FilterCategoryDescriptor[] = [
  {
    id: 'dateRange',
    defaultOptionId: 'last_30_days',
    optionIds: ['last_7_days', 'last_30_days', 'current_month', 'previous_month', DATE_RANGE_CUSTOM]
  },
  {
    id: 'lifecycle',
    defaultOptionId: 'all',
    optionIds: ['all', 'new', 'active', 'at_risk', 'dormant', 'churned', 'reactivated']
  },
  {
    id: 'purchaseBehaviour',
    defaultOptionId: 'all',
    optionIds: [
      'all',
      'first_time_buyer',
      'frequent_buyer',
      'occasional_buyer',
      'lapsed_buyer',
      'non_buyer'
    ]
  },
  {
    id: 'customerValue',
    defaultOptionId: 'all',
    optionIds: ['all', 'low', 'medium', 'high', 'very_high']
  },
  {
    id: 'engagement',
    defaultOptionId: 'all',
    optionIds: ['all', 'high_engagement', 'moderate_engagement', 'low_engagement', 'no_engagement']
  },
  {
    id: 'productRelationship',
    defaultOptionId: 'all',
    optionIds: ['all', 'non_holder', 'single_product_customer', 'multi_product_customer']
  },
  {
    id: 'demographics',
    defaultOptionId: 'all_permitted',
    optionIds: ['all_permitted', 'age_band', 'gender', 'nationality', 'income_band', 'ethnicity']
  },
  {
    id: 'consentStatus',
    defaultOptionId: 'all',
    optionIds: ['all', 'consented', 'not_consented', 'withdrawn']
  }
];

export const DEFAULT_FILTER_SELECTIONS: Record<IntelligenceFilterId, string> =
  FILTER_CATEGORIES.reduce(
    (defaults, category) => ({ ...defaults, [category.id]: category.defaultOptionId }),
    {} as Record<IntelligenceFilterId, string>
  );

const AGE_BAND_MIN = 1;
const AGE_BAND_MAX = 100;

const ageBandValues = (): string[] =>
  Array.from({ length: AGE_BAND_MAX - AGE_BAND_MIN + 1 }, (unused, index) =>
    String(AGE_BAND_MIN + index).padStart(2, '0')
  );

// The static half of the Demographics sub-select values. Figma names no enumerable list for ANY
// dimension — it draws one collapsed pill per row and no open menu anywhere
// (design/filter-demographics-open-devmode-2026-08-12.md § 1a) — so the values come from the
// dashboard's own customer-attribute mappings where those exist, and from the lists below where
// they do not. `useDemographicOptions` is what joins the two.
export const DEMOGRAPHIC_SUB_VALUES: Record<string, string[]> = {
  age_band: ageBandValues(),
  // All four are specified by the designer, verbatim, in Figma comment `#98` on the Demographics
  // card `621:3991`. They are not invented and not a stand-in — an earlier note here said only the
  // first was confirmed, because a layer-tree read finds only the one value the collapsed pill
  // draws. The spec lived in the comment, which no layer search reaches.
  income_band: ['0-10,000', '10,001-50,000', '50,001-100,000', '100,001+'],
  // PLACEHOLDER — dummy values so the control renders and the UI is reviewable. Requested by the
  // user (2026-08-12) because no `personal_property_mappings` endpoint serves nationality, unlike
  // gender and ethnicity. NOT tenant data and NOT from the design: replace with FR-owner values.
  nationality: ['Singapore', 'Malaysia', 'Indonesia', 'Philippines', 'Thailand']
};

// `gender` and `ethnicity` are absent above on purpose: `useDemographicOptions` serves them live
// from the tenant's customer-attribute mappings.
//
// Dimensions whose values above are stand-ins, not authoritative. Kept as data so the UI can mark
// them and a test can prove nothing invented has quietly become permanent.
export const PLACEHOLDER_DIMENSIONS = ['nationality'];

// `Age band` is the one dimension the design gives two controls, a from and a to.
export const AGE_BAND_RANGE_SEPARATOR = '-';

export const decodeAgeBand = (subValue?: string): [string, string] | null => {
  if (!subValue) {
    return null;
  }

  const [from, to] = subValue.split(AGE_BAND_RANGE_SEPARATOR);

  return from && to ? [from, to] : null;
};

export default FILTER_CATEGORIES;
